import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Button } from '@/components/Button';
import { ModalInfo } from '@/components/_modals/ModalInfo';
import { SvgIcon } from '@/components/SvgIcon';
import { NotificationItem } from '@/redux/models';
import { renderMessageMarkup } from '@/api/amlbotAPI/renderMessageMarkup';

import {
  DAYJS_LOCALE,
  ICON_BY_TYPE,
  useNotificationCopy,
  useNotificationDestination,
} from './notificationDisplay';
import styles from './NotificationModal.module.sass';

interface NotificationModalProps {
  item: NotificationItem;
  // Follows an internal path. The caller routes it, then the popup closes.
  onNavigate: (href: string) => void;
  onClose: () => void;
}

/**
 * One notification in full, opened by clicking it in the bell or on the All
 * Notifications page (QA). The list shows a preview; this is where the whole message
 * is read and its destination followed.
 */
const NotificationModalContent: FC<NotificationModalProps> = ({
  item,
  onNavigate,
  onClose,
}) => {
  const { i18n } = useTranslation();
  const copy = useNotificationCopy(item);
  const destination = useNotificationDestination(item);

  const follow = (href: string, external: boolean) => {
    if (external) {
      // A new tab, as for a link inside a campaign message: the cabinet stays open.
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    onNavigate(href);
    onClose();
  };

  return (
    <>
      <div className={styles.title}>{copy.title}</div>
      <div className={styles.meta}>
        <span className={styles.icon}>
          <SvgIcon name={ICON_BY_TYPE[item.type] || 'notification'} />
        </span>
        <span>
          {dayjs(item.createdAt)
            .locale(DAYJS_LOCALE[i18n.language] || 'en')
            .format('MMM D YYYY HH:mm')}
        </span>
      </div>

      <div className={styles.body} data-testid="notification-modal-body">
        {item.kind === 'campaign'
          ? renderMessageMarkup(copy.body, {
              onLinkActivate: (href, external) => {
                // An external link opens its own new tab via target=_blank.
                if (!external) follow(href, false);
              },
            })
          : copy.body}
      </div>

      {destination && (
        <Button
          type="primary"
          size="l"
          corner="rect"
          fullwidth
          className={styles.action}
          onClick={() => follow(destination.href, destination.external)}
        >
          {destination.label}
        </Button>
      )}
    </>
  );
};

export const NotificationModal: FC<
  Omit<NotificationModalProps, 'item'> & { item: NotificationItem | null }
> = ({ item, onNavigate, onClose }) => (
  <ModalInfo isOpen={item !== null} className={styles.modal} onClose={onClose}>
    {item && (
      <NotificationModalContent
        item={item}
        onNavigate={onNavigate}
        onClose={onClose}
      />
    )}
  </ModalInfo>
);
